/**
 * Invoice Utility Functions
 * Helper functions for formatting and manipulation
 *
 * @author SAUBHAGYA Development Team
 * @since US-INV-001
 */

// ============================================
// FORMATTING FUNCTIONS
// ============================================

/**
 * Format currency amount to INR
 * @param amount Amount in number
 * @returns Formatted currency string (₹X,XXX.XX)
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format date string to readable format
 * @param dateString ISO date string
 * @returns Formatted date (dd MMM yyyy)
 */
export function formatDate(dateString: string): string {
  if (!dateString) return '-';

  const date = new Date(dateString);
  return new Intl.DateTimeFormat('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

/**
 * Format date to ISO string (for inputs)
 * @param dateString Any date format
 * @returns ISO string (yyyy-MM-dd)
 */
export function formatDateToISO(dateString: string): string {
  if (!dateString) return '';

  const date = new Date(dateString);
  return date.toISOString().split('T')[0];
}

/**
 * Parse date string to Date object
 * @param dateString ISO or readable date string
 * @returns Date object
 */
export function parseDate(dateString: string): Date {
  return new Date(dateString);
}

// ============================================
// FILTER BUILDER FUNCTIONS
// ============================================

/**
 * Build query string from filter params
 * @param params Query parameters
 * @returns URL query string
 */
export function buildQueryString(params: Record<string, any>): string {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, String(v)));
      } else {
        searchParams.set(key, String(value));
      }
    }
  });

  return searchParams.toString();
}

// ============================================
// STATUS HELPER FUNCTIONS
// ============================================

/**
 * Get readable status label
 * @param status Invoice status
 * @returns Readable status label
 */
export function getStatusLabel(status: string): string {
  const labels: Record<string, string> = {
    DRAFT: 'Draft',
    ISSUED: 'Issued',
    PAID: 'Paid',
    PARTIALLY_PAID: 'Partially Paid',
    OVERDUE: 'Overdue',
    CANCELLED: 'Cancelled',
  };
  return labels[status] || status;
}

/**
 * Get status color/badge class
 * @param status Invoice status
 * @returns CSS class for badge
 */
export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    DRAFT: 'bg-slate-100 text-slate-800',
    ISSUED: 'bg-blue-100 text-blue-800',
    PAID: 'bg-green-100 text-green-800',
    PARTIALLY_PAID: 'bg-amber-100 text-amber-800',
    OVERDUE: 'bg-red-100 text-red-800',
    CANCELLED: 'bg-slate-100 text-slate-800',
  };
  return colors[status] || 'bg-slate-100 text-slate-800';
}

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validate date range
 * @param from From date
 * @param to To date
 * @returns true if valid, false otherwise
 */
export function isValidDateRange(from: string, to: string): boolean {
  if (!from || !to) return true; // Either can be empty
  const fromDate = new Date(from);
  const toDate = new Date(to);
  return fromDate <= toDate;
}

/**
 * Check if amount is valid for invoice
 * @param amount Amount to validate
 * @returns true if valid, false otherwise
 */
export function isValidAmount(amount: number): boolean {
  return amount > 0 && amount <= 99999999.99;
}
